
import { useEffect, useRef, useCallback } from "react";
import { Dealer } from "@/types/dealer";
import { Coordinates } from "../types";
import { 
  sortDealersByPriority
} from "../utils";
import { checkCacheVersion, getPrefetchedDealersFromCache } from "../cache";
import { hasPrefetchedDealers } from "../prefetch";
import { fetchRecommendedDealersData } from "../fetchData";

/**
 * Hook to handle initial data loading for recommended dealers
 * FIXED: Simplified to use single useEffect to prevent initialization conflicts
 */
export function useInitialDataEffect(
  selectedMetal: string,
  coordinates: Coordinates | null | undefined,
  setDealers: (dealers: Dealer[]) => void,
  setLoading: (loading: boolean) => void,
  setIsError: (isError: boolean) => void,
  setHasAttemptedLoad: (hasAttempted: boolean) => void,
  dealers?: Dealer[]
): {
  initialLoadDone: React.RefObject<boolean>;
  freshDataLoading: { value: boolean };
  prefetchAttempted: React.RefObject<boolean>;
  fetchController: React.RefObject<AbortController | null>;
} {
  const initialLoadDone = useRef(false);
  const prefetchAttempted = useRef(false);
  const fetchController = useRef<AbortController | null>(null);
  const dataFetched = useRef(false);
  const lastMetal = useRef<string | null>(null);
  const mounted = useRef(true);
  
  // Use a mutable object instead of a ref for freshDataLoading
  const freshDataLoading = useRef({ value: false });
  
  // Simplified load function - immediate rendering
  const loadRecommendedDealers = useCallback(async () => {
    if (!mounted.current) {
      console.log("⚠️ Component unmounted, skipping load");
      return;
    }
    
    console.log("🔍 FIXED: loadRecommendedDealers called", {
      selectedMetal,
      coordinates: coordinates ? { lat: coordinates.latitude, lng: coordinates.longitude } : null,
      dealersCount: dealers?.length || 0,
      initialLoadDone: initialLoadDone.current,
      freshDataLoading: freshDataLoading.current.value
    });
    
    // Prevent multiple simultaneous loads
    if (freshDataLoading.current.value) {
      console.log("⏳ Load already in progress");
      return;
    }
    
    // Check if metal changed
    if (lastMetal.current && lastMetal.current !== selectedMetal) {
      console.log("🔄 Metal changed, resetting state");
      initialLoadDone.current = false;
      dataFetched.current = false;
    }
    lastMetal.current = selectedMetal;
    
    // Cancel any previous request
    if (fetchController.current) {
      fetchController.current.abort();
    }
    fetchController.current = new AbortController();
    
    try {
      freshDataLoading.current.value = true;
      
      if (mounted.current) {
        setLoading(true);
        setIsError(false);
      }
      
      // Try prefetched data first
      const prefetchedDealers = getPrefetchedDealersFromCache();
      if (prefetchedDealers && prefetchedDealers.length > 0 && mounted.current) {
        console.log("✅ Using prefetched data:", prefetchedDealers.length);
        const sortedDealers = sortDealersByPriority(prefetchedDealers, coordinates);
        setDealers(sortedDealers);
        setLoading(false);
        initialLoadDone.current = true;
      }
      
      // Fetch fresh data
      console.log("🔄 Fetching fresh data");
      const freshDealers = await fetchRecommendedDealersData(
        selectedMetal, 
        coordinates, 
        true,
        false,
        fetchController.current.signal
      );
      
      console.log("✅ Fresh dealers fetched:", freshDealers.length);
      
      if (Array.isArray(freshDealers) && freshDealers.length > 0 && !fetchController.current.signal.aborted && mounted.current) {
        const sortedFreshDealers = sortDealersByPriority(freshDealers, coordinates);
        
        // IMMEDIATE update - no setTimeout!
        setDealers(sortedFreshDealers);
        console.log("✅ IMMEDIATE: Dealers state updated", {
          newDealersCount: sortedFreshDealers.length
        });
        
        dataFetched.current = true;
      } else if (!initialLoadDone.current && mounted.current) {
        console.log("❌ No dealers found");
        setIsError(true);
      }
      
      if (mounted.current) {
        initialLoadDone.current = true;
        setLoading(false);
        setHasAttemptedLoad(true);
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🚫 Request cancelled');
        return;
      }
      
      console.error("❌ Error loading dealers:", error);
      if (mounted.current) {
        setIsError(true);
        setLoading(false);
        initialLoadDone.current = true;
        setHasAttemptedLoad(true);
      }
    } finally {
      freshDataLoading.current.value = false;
    }
  }, [selectedMetal, coordinates, setDealers, setLoading, setIsError, dealers?.length]);
  
  // FIXED: Single useEffect to prevent conflicts
  useEffect(() => {
    mounted.current = true;
    checkCacheVersion();
    
    console.log("🚀 useInitialDataEffect: Single effect triggered", {
      selectedMetal,
      initialLoadDone: initialLoadDone.current,
      freshDataLoading: freshDataLoading.current.value
    });
    
    // Only load if not already done and not currently loading
    if (!initialLoadDone.current && !freshDataLoading.current.value) {
      console.log("🔄 Starting initial load");
      loadRecommendedDealers();
    }
    
    // Cleanup function to abort any pending requests when component unmounts
    return () => {
      mounted.current = false;
      if (fetchController.current) {
        fetchController.current.abort();
      }
    };
  }, [selectedMetal, loadRecommendedDealers]);

  return {
    initialLoadDone,
    freshDataLoading: freshDataLoading.current,
    prefetchAttempted,
    fetchController
  };
}
